import { ExternalLink } from "lucide-react";
import { type ReactNode } from "react";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Sparkline } from "@/components/cinch/Sparkline";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";

import { useFeatureDetail, type FeatureDetail } from "../api";
import { UsageTimeline } from "./UsageTimeline";

/**
 * Drill-down for one feature, opened from the Overview table.
 *
 * Quick view (row click) answers "is this healthy?" — totals, movement, a
 * sparkline, who's using it, when it was last opened.
 *
 * Expanded view (feature-name click) answers "what's going on in here?" — the
 * full daily chart, every user with last-seen, which routes *inside* the
 * feature get used, and recent errors. Those cost two extra PostHog queries,
 * so they're only fetched at this depth.
 */
export function FeatureDrawer({
  featureKey,
  label,
  navGroup,
  days,
  expanded,
  onOpenChange,
  onExpandedChange,
  onClose,
}: {
  featureKey: string | null;
  label: string | null;
  navGroup: string | null;
  days: number;
  expanded: boolean;
  onOpenChange: (open: boolean) => void;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useFeatureDetail(featureKey, days, expanded);

  return (
    <EntityDrawer
      open={featureKey != null}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={label ?? featureKey ?? ""}
      subtitle={
        featureKey ? (
          <span className="flex items-center gap-2">
            <span>{navGroup ?? "—"}</span>
            <span aria-hidden>·</span>
            <code className="rounded bg-muted px-1 text-xs">{featureKey}</code>
          </span>
        ) : null
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error ? (
          <ErrorState error={error} />
        ) : data ? (
          <Body data={data} expanded={expanded} />
        ) : null}
      </div>
    </EntityDrawer>
  );
}

function ErrorState({ error }: { error: unknown }) {
  const notConfigured = error instanceof ApiError && error.status === 409;
  return (
    <div
      className={`rounded-lg border p-4 text-sm ${
        notConfigured ? "border-border bg-muted/30" : "border-danger/40 bg-danger/5"
      }`}
    >
      <div className="font-semibold">
        {notConfigured ? "Read-back isn't configured" : "Couldn't load this feature"}
      </div>
      <p className="mt-1 text-muted-foreground">
        {error instanceof Error ? error.message : String(error)}
      </p>
    </div>
  );
}

function Body({ data, expanded }: { data: FeatureDetail; expanded: boolean }) {
  const spark = data.daily.map((d) => d.events);
  // Expanded lists everyone; the quick view keeps it to the busiest three.
  const shown = expanded ? data.by_user : data.by_user.slice(0, 3);
  const identified = data.by_user.length;
  const unidentified = Math.max(0, data.users - identified);

  return (
    <>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Stat label="Opens">{data.events.toLocaleString()}</Stat>
        <Stat label="People">{data.users}</Stat>
        <Stat label="vs prior">
          <DeltaCell
            value={data.prior_events === 0 && data.events === 0 ? null : data.delta_events}
            format={(n) => Math.abs(n).toLocaleString()}
          />
        </Stat>
        <Stat label="Last opened">{relativeTime(data.last_seen)}</Stat>
      </dl>

      {/* The sparkline is quick-view only. In the expanded view the full
          timeline chart below plots the same series, and showing both was
          just the same data twice. */}
      {!expanded ? (
        spark.some((n) => n > 0) ? (
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              Opens per day
            </div>
            <Sparkline points={spark} tone="brand" height={36} />
          </div>
        ) : (
          <StatusBadge value="inactive" label="No activity in this window" />
        )
      ) : null}

      {data.users > 0 ? (
        <section className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {expanded ? "Who opened it" : "Top users"}
            </div>
            {/* The header count is distinct people from PostHog; the list is
                only those whose id resolves to a cinch_ops account. When they
                disagree, say so — a list quietly shorter than the number above
                it reads as a bug. */}
            {identified < data.users ? (
              <span className="text-[11px] text-muted-foreground">
                {identified} of {data.users} identified
              </span>
            ) : null}
          </div>

          {shown.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {shown.map((u) => (
                <li
                  key={u.user_id}
                  className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-1.5 text-sm"
                >
                  <span className="min-w-0 truncate">{u.name ?? u.email ?? u.user_id}</span>
                  <span className="shrink-0 text-muted-foreground">
                    <span className="tabular-nums">{u.events}</span>
                    {u.last_seen ? (
                      <span className="ml-2 text-xs">{relativeTime(u.last_seen)}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {unidentified > 0 ? (
            <p className="text-xs text-muted-foreground">
              {unidentified} session{unidentified === 1 ? "" : "s"} from an account this
              environment doesn&apos;t recognise — usually someone signed in against a
              different database (local or dev) whose user id doesn&apos;t exist here.
            </p>
          ) : null}

          {!expanded && data.by_user.length > 3 ? (
            <p className="text-xs text-muted-foreground">
              +{data.by_user.length - 3} more — open the full view.
            </p>
          ) : null}
        </section>
      ) : null}

      {expanded ? (
        <>
          <UsageTimeline daily={data.daily} />

          {data.by_path.length > 0 ? (
            <section className="flex flex-col gap-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Where inside the feature
              </div>
              <p className="text-xs text-muted-foreground">
                Route shapes, not records — ids are stripped before anything leaves the
                browser.
              </p>
              <ul className="flex flex-col gap-1">
                {data.by_path.map((p) => (
                  <li
                    key={p.path}
                    className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-1.5 text-sm"
                  >
                    <code className="min-w-0 truncate text-xs">{p.path}</code>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {p.events}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {data.errors.length > 0 ? (
            <section className="flex flex-col gap-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Recent errors
              </div>
              <ul className="flex flex-col gap-1">
                {data.errors.map((e, i) => (
                  <li
                    key={`${e.kind}-${i}`}
                    className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-danger">{e.kind}</span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {e.events}×
                      </span>
                    </div>
                    {e.message ? (
                      <p className="mt-0.5 break-words text-xs text-muted-foreground">
                        {e.message}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}

      {data.posthog_url ? (
        <a
          href={data.posthog_url}
          target="_blank"
          rel="noreferrer"
          className="flex w-fit items-center gap-1.5 text-sm text-power underline underline-offset-2"
        >
          Explore in PostHog
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : null}
    </>
  );
}

/** Read-only label/value pair — mirrors the contracts RecordDrawer helper. */
function Stat({ label, children }: { label: string; children?: ReactNode }) {
  const empty = children == null || children === "" || children === "—";
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm text-foreground">{empty ? "—" : children}</dd>
    </div>
  );
}

/** "3h ago" / "2d ago". Absolute dates are noise at this granularity. */
function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const then = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(then)) return "—";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
